import type {
  ApiClient,
  LegacyKuduResponseEnvelope,
} from '@/api/client.types';

import {
  normalizeStaffInvite,
  normalizeStaffInviteView,
  STAFF_ROLE_CODES,
  type RawAcceptCourseStaffInviteResponse,
  type RawCreateCourseStaffInviteResponse,
  type RawGetCourseStaffInviteResponse,
  type RawGetCourseStaffInvitesResponse,
  type RawRenewCourseStaffInviteResponse,
  type StaffInvite,
  type StaffInviteView,
  type StaffRole,
} from './staffInviteTypes';

export interface CreateStaffInviteInput {
  email: string;
  firstName: string;
  lastName: string;
  role: StaffRole;
}

export interface CreatedStaffInvite {
  alreadyPending: boolean;
  invite: StaffInvite;
}

type MaybeEnvelope<T> = T | LegacyKuduResponseEnvelope<T>;

function unwrap<T>(payload: MaybeEnvelope<T> | null | undefined): T | null {
  if (payload && typeof payload === 'object' && 'Response' in payload) {
    return (payload as LegacyKuduResponseEnvelope<T>).Response ?? null;
  }

  return (payload as T | null | undefined) ?? null;
}

function requireInvite(invite: StaffInvite | null): StaffInvite {
  if (!invite) {
    throw new Error('The server returned an incomplete invitation.');
  }

  return invite;
}

// Instructor-side calls, and accepting, go through invokeKuduMethod with the
// signed-in user's credentials. Looking up an invite by its key is public: the
// invitee may have no account yet, so it sends no credentials at all.
export function createStaffInviteService(client: ApiClient) {
  return {
    async acceptInvite(input: { key: string; userId: string }): Promise<string> {
      const response = await client.invokeKuduMethod<
        MaybeEnvelope<RawAcceptCourseStaffInviteResponse>,
        { key: string; user_id: string }
      >('AcceptCourseStaffInvite', { key: input.key, user_id: input.userId });
      const payload = unwrap(response);
      const courseId = (payload?.course_id ?? payload?.courseId ?? '').trim();

      if (!courseId) {
        throw new Error('The server did not say which course you joined.');
      }

      return courseId;
    },

    async cancelInvite(input: { courseId: string; key: string; userId: string }) {
      await client.invokeKuduMethod('CancelCourseStaffInvite', {
        course_id: input.courseId,
        key: input.key,
        user_id: input.userId,
      });
    },

    async createInvite(
      input: CreateStaffInviteInput & { courseId: string; userId: string },
    ): Promise<CreatedStaffInvite> {
      const response = await client.invokeKuduMethod<
        MaybeEnvelope<RawCreateCourseStaffInviteResponse>,
        Record<string, number | string>
      >('CreateCourseStaffInvite', {
        course_id: input.courseId,
        email: input.email,
        first_name: input.firstName,
        last_name: input.lastName,
        role: STAFF_ROLE_CODES[input.role],
        user_id: input.userId,
      });
      const payload = unwrap(response);

      return {
        alreadyPending: Boolean(payload?.already_pending ?? payload?.alreadyPending),
        invite: requireInvite(normalizeStaffInvite(payload?.invite)),
      };
    },

    async listInvites(input: { courseId: string; userId: string }): Promise<StaffInvite[]> {
      const response = await client.invokeKuduMethod<
        MaybeEnvelope<RawGetCourseStaffInvitesResponse>,
        { course_id: string; user_id: string }
      >('GetCourseStaffInvites', { course_id: input.courseId, user_id: input.userId });

      return (unwrap(response)?.invites ?? [])
        .map((invite) => normalizeStaffInvite(invite))
        .filter((invite): invite is StaffInvite => invite !== null);
    },

    async lookupInvite(key: string): Promise<StaffInviteView> {
      const response = await client.post<
        MaybeEnvelope<RawGetCourseStaffInviteResponse>
      >('/_ah/KuduAPI/GetCourseStaffInvite', { Request: { key } }, { auth: 'none' });

      return normalizeStaffInviteView(unwrap(response));
    },

    async removeStaffMember(input: {
      courseId: string;
      role: StaffRole;
      staffUserId: string;
      userId: string;
    }) {
      await client.invokeKuduMethod('RemoveCourseStaffMember', {
        course_id: input.courseId,
        role: STAFF_ROLE_CODES[input.role],
        staff_user_id: input.staffUserId,
        user_id: input.userId,
      });
    },

    async renewInvite(input: {
      courseId: string;
      key: string;
      userId: string;
    }): Promise<StaffInvite> {
      const response = await client.invokeKuduMethod<
        MaybeEnvelope<RawRenewCourseStaffInviteResponse>,
        { course_id: string; key: string; user_id: string }
      >('RenewCourseStaffInvite', {
        course_id: input.courseId,
        key: input.key,
        user_id: input.userId,
      });

      return requireInvite(normalizeStaffInvite(unwrap(response)?.invite));
    },
  };
}

export type StaffInviteService = ReturnType<typeof createStaffInviteService>;
